import { useRecordModal } from 'hooks/useRecordModal';
import { RefundChargebackReportSummaryAmountField } from 'pages/Finance/RefundChargebackReport/RefundChargebackReportSummary/RefundChargebackReportSummaryAmountField/RefundChargebackReportSummaryAmountField';
import { RefundChargebackReportSummaryMerchantAccountField } from 'pages/Finance/RefundChargebackReport/RefundChargebackReportSummary/RefundChargebackReportSummaryMerchantAccountField/RefundChargebackReportSummaryMerchantAccountField';
import { ListContextProvider, useList, useListContext } from 'react-admin';
import { Stack } from 'shared/mui/Stack/Stack';
import { Typography } from 'shared/mui/Typography';
import { FunctionField } from 'shared/react-admin/Fields';
import { MerchantAccountShowDialog } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialog';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import {
  RefundChargebackReportSummary,
  RefundChargebackReportSummaryMerchantAccount,
} from 'types/embedded/refundChargebackReport';

import { DatargidWithFooterStyled } from './RefundChargebackReportSummaryDatagrid.styled';
import { RefundChargebackReportSummaryDatagridFooter } from './RefundChargebackReportSummaryDatagridFooter/RefundChargebackReportSummaryDatagridFooter';

type Props = { summary: RefundChargebackReportSummary };

export const RefundChargebackReportSummaryDatagrid: React.FC<Props> = ({ summary }) => {
  const { isLoading, filterValues } = useListContext();

  const {
    record: merchantAccount,
    handleCloseRecordModal: handleCloseMerchantAccountModal,
    openRecordModal: openMerchantAccountModal,
    handleOpenRecordModal: handleOpenShowDialog,
  } = useRecordModal<MerchantAccount>();

  const context = useList({
    data: summary?.merchantAccounts || [],
    isLoading,
  });

  return (
    <ListContextProvider value={context}>
      <DatargidWithFooterStyled
        bulkActionButtons={false}
        data={summary?.merchantAccounts || []}
        footer={
          <RefundChargebackReportSummaryDatagridFooter
            conversionCurrency={filterValues?.conversionCurrency}
            data={summary}
          />
        }
        isLoading={isLoading}
        resource="merchant_accounts"
      >
        <RefundChargebackReportSummaryMerchantAccountField
          label="Счет мёрчанта"
          onClick={handleOpenShowDialog}
          source="id"
        />
        <FunctionField
          label="Валюта счета"
          render={(record: RefundChargebackReportSummaryMerchantAccount) => (
            <Stack>
              <Typography variant="body2">{record?.currency.alphaCode}</Typography>
              <Typography color="secondary.main" variant="body2">
                {record?.currency.name}
              </Typography>
            </Stack>
          )}
          source="currency"
        />
        <FunctionField
          label="Количество рефандов"
          render={(record: RefundChargebackReportSummaryMerchantAccount) => (
            <Typography variant="body2">{record?.refundCount}</Typography>
          )}
          source="refundCount"
        />
        <RefundChargebackReportSummaryAmountField
          conversionValueKey="conversionRefundAmount"
          currency={filterValues?.conversionCurrency}
          label="Сумма возврата по рефандам"
          originalValueKey="originalRefundAmount"
          source="originalRefundAmount"
        />
        <RefundChargebackReportSummaryAmountField
          conversionValueKey="conversionRefundCommissionAmount"
          currency={filterValues?.conversionCurrency}
          label="Сумма комиссий по рефандам"
          originalValueKey="originalRefundCommissionAmount"
          source="originalRefundCommissionAmount"
        />
        <RefundChargebackReportSummaryAmountField
          conversionValueKey="conversionRefundTotalAmount"
          currency={filterValues?.conversionCurrency}
          label="Итого по рефандам"
          originalValueKey="originalRefundTotalAmount"
          source="originalRefundTotalAmount"
        />
        <FunctionField
          label="Количество чарджбеков"
          render={(record: RefundChargebackReportSummaryMerchantAccount) => (
            <Typography variant="body2">{record?.chargebackCount}</Typography>
          )}
          source="chargebackCount"
        />
        <RefundChargebackReportSummaryAmountField
          conversionValueKey="conversionChargebackAmount"
          currency={filterValues?.conversionCurrency}
          label="Сумма возврата по чарджбекам"
          originalValueKey="originalChargebackAmount"
          source="originalChargebackAmount"
        />
        <RefundChargebackReportSummaryAmountField
          conversionValueKey="conversionChargebackCommissionAmount"
          currency={filterValues?.conversionCurrency}
          label="Сумма комиссий по чарджбекам"
          originalValueKey="originalChargebackCommissionAmount"
          source="originalChargebackCommissionAmount"
        />
        <RefundChargebackReportSummaryAmountField
          conversionValueKey="conversionChargebackTotalAmount"
          currency={filterValues?.conversionCurrency}
          label="Итого по чарджбекам"
          originalValueKey="originalChargebackTotalAmount"
          source="originalChargebackTotalAmount"
        />
      </DatargidWithFooterStyled>
      {merchantAccount && (
        <MerchantAccountShowDialog
          merchantAccount={merchantAccount}
          onClose={handleCloseMerchantAccountModal}
          open={openMerchantAccountModal}
        />
      )}
    </ListContextProvider>
  );
};
